import { useState, useEffect, useRef, useCallback } from 'react';
import { useSharedWS } from './useSocket';

interface DashMetrics {
  totalRuns: number;
  successes: number;
  failures: number;
  retries: number;
  bees: Record<string, {
    totalRuns: number;
    successes: number;
    failures: number;
    avgLatency: number;
  }>;
}

interface DashAlert {
  id: string;
  type: 'warning' | 'error' | 'info';
  message: string;
  timestamp: string;
  severity: 'low' | 'medium' | 'high';
}

export interface InternalDashSocketState {
  isConnected: boolean;
  connectionState: 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED';
  retryCount: number;
  lastError?: string;
  metrics?: DashMetrics;
  alerts?: DashAlert[];
}

interface UseInternalDashSocketOptions {
  url?: string;
  sessionId?: string;
  onMessage?: (message: any) => void;
  onMetricsUpdate?: (metrics: DashMetrics) => void;
  onAlert?: (alert: DashAlert) => void;
  onBeeLogChunk?: (chunk: any) => void;
  onConnectionChange?: (isConnected: boolean) => void;
  onOpen?: () => void;
  onError?: (error: string) => void;
}

interface DashCommand {
  type: string;
  data?: unknown;
  chunk?: any;
}

export function useInternalDashSocket(options: UseInternalDashSocketOptions): InternalDashSocketState & {
  sendCommand: (command: DashCommand) => void;
  disconnect: () => void;
  status: 'idle' | 'connecting' | 'open' | 'error';
}{
  const { sessionId, onMessage, onMetricsUpdate, onAlert, onBeeLogChunk, onConnectionChange, onOpen, onError } = options;
  
  const [state, setState] = useState<InternalDashSocketState>({
    isConnected: false,
    connectionState: 'CONNECTING',
    retryCount: 0,
    alerts: [],
  });
  
  const [status, setStatus] = useState<'idle' | 'connecting' | 'open' | 'error'>('connecting');

  const log = useCallback((message: string, level: 'info' | 'warn' | 'error' = 'info') => {
    const timestamp = new Date().toISOString();
    const prefix = `[INTERNAL-DASH-SOCKET]`;
    console[level](`${timestamp} ${prefix} ${message}`);
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      
      onMessage?.(message);
      
      switch (message.type) {
        case 'pong':
          log('Received pong');
          break;
          
        case 'metrics_update':
          setState(prev => ({ ...prev, metrics: message.data }));
          onMetricsUpdate?.(message.data);
          break;
          
        case 'alert':
          setState(prev => ({ 
            ...prev, 
            alerts: [...(prev.alerts || []), message.data] 
          }));
          onAlert?.(message.data);
          break;
          
        case 'beeLogChunk':
          onBeeLogChunk?.(message.chunk);
          break;
          
        case 'system_status':
          log(`System status: ${message.status}`);
          break;
          
        default:
          log(`Unknown message type: ${message.type}`);
          break;
      }
    } catch (error) {
      log(`Error parsing message: ${error}`, 'error');
    }
  }, [log, onMessage, onMetricsUpdate, onAlert, onBeeLogChunk]);

  const { send, socket } = useSharedWS('/chat', handleMessage);

  useEffect(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      log('Connected successfully');
      setState(prev => ({ 
        ...prev, 
        isConnected: true,
        connectionState: 'OPEN',
        retryCount: 0,
        lastError: undefined 
      }));
      setStatus('open');
      onConnectionChange?.(true);
      onOpen?.();
      
      send({ type: 'get_metrics' });
    } else if (socket?.readyState === WebSocket.CONNECTING) {
      setState(prev => ({ ...prev, connectionState: 'CONNECTING' }));
      setStatus('connecting');
    } else {
      setState(prev => ({ 
        ...prev, 
        isConnected: false,
        connectionState: 'CLOSED'
      }));
      setStatus('error');
      onConnectionChange?.(false);
    }
  }, [socket, send, log, onConnectionChange, onOpen]);

  const sendCommand = useCallback((command: DashCommand) => {
    send(command);
  }, [send]);

  const disconnect = useCallback(() => {
    log('Manually disconnecting');
  }, [log]);

  return {
    ...state,
    sendCommand,
    disconnect,
    status,
  };
}
